import { getConfig } from "./config";

interface OAuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

const TOKENS_KEY = "otto-voice-reports:tokens";
const PKCE_VERIFIER_KEY = "otto-voice-reports:pkce-verifier";
const POST_AUTH_KEY = "otto-voice-reports:post-auth-path";

export function loadTokens(): OAuthTokens | null {
  const raw = localStorage.getItem(TOKENS_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as OAuthTokens; } catch { return null; }
}

export function saveTokens(t: OAuthTokens): void {
  localStorage.setItem(TOKENS_KEY, JSON.stringify(t));
}

export function clearTokens(): void {
  localStorage.removeItem(TOKENS_KEY);
}

export function tokenValid(t: OAuthTokens | null, skewSeconds = 60): boolean {
  if (!t) return false;
  return t.expiresAt > Math.floor(Date.now() / 1000) + skewSeconds;
}

async function sha256Base64Url(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function generateVerifier(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((n) => n.toString(16).padStart(2, "0")).join("");
}

export async function beginLogin(currentPath: string): Promise<void> {
  const cfg = getConfig();
  const verifier = generateVerifier();
  localStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  localStorage.setItem(POST_AUTH_KEY, currentPath);
  const challenge = await sha256Base64Url(verifier);
  const url = new URL(`${cfg.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set("client_id", cfg.cognitoClientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("redirect_uri", `${cfg.publicOrigin}/oauth/callback`);
  url.searchParams.set("identity_provider", cfg.o365IdpName);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);
  window.location.assign(url.toString());
}

export async function exchangeCode(code: string): Promise<OAuthTokens> {
  const cfg = getConfig();
  const verifier = localStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) throw new Error("Missing PKCE verifier - please sign in again");
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: cfg.cognitoClientId,
    code,
    redirect_uri: `${cfg.publicOrigin}/oauth/callback`,
    code_verifier: verifier,
  });
  const resp = await fetch(`${cfg.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!resp.ok) {
    const errBody = await resp.text().catch(() => "");
    throw new Error(`Token exchange failed (${resp.status}): ${errBody}`);
  }
  const data = await resp.json() as { id_token: string; access_token: string; refresh_token: string; expires_in: number };
  const tokens: OAuthTokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Math.floor(Date.now() / 1000) + data.expires_in,
  };
  saveTokens(tokens);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  return tokens;
}

export async function refreshAccessToken(): Promise<OAuthTokens | null> {
  const cfg = getConfig();
  const existing = loadTokens();
  if (!existing?.refreshToken) return null;
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: cfg.cognitoClientId,
    refresh_token: existing.refreshToken,
  });
  const resp = await fetch(`${cfg.cognitoDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!resp.ok) {
    clearTokens();
    return null;
  }
  const data = await resp.json() as { id_token: string; access_token: string; expires_in: number };
  const tokens: OAuthTokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: existing.refreshToken,
    expiresAt: Math.floor(Date.now() / 1000) + data.expires_in,
  };
  saveTokens(tokens);
  return tokens;
}

export function consumePostAuthPath(): string {
  const path = localStorage.getItem(POST_AUTH_KEY);
  localStorage.removeItem(POST_AUTH_KEY);
  return path && path !== "/oauth/callback" ? path : "/";
}

export async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  let tokens = loadTokens();
  if (!tokenValid(tokens)) {
    tokens = await refreshAccessToken();
  }
  if (!tokens) throw new Error("Not authenticated");
  const headers = new Headers(init.headers);
  headers.set("authorization", `Bearer ${tokens.idToken}`);
  return fetch(path, { ...init, headers });
}

export interface ClaimsUser {
  email: string | null;
  givenName: string | null;
  familyName: string | null;
  sub: string;
}

export function readClaimsFromIdToken(): ClaimsUser | null {
  const tokens = loadTokens();
  if (!tokens?.idToken) return null;
  const parts = tokens.idToken.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4 || 4)) % 4);
    const parsed = JSON.parse(atob(padded)) as Record<string, unknown>;
    const s = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);
    return {
      email: s(parsed.email),
      givenName: s(parsed.given_name),
      familyName: s(parsed.family_name),
      sub: s(parsed.sub) ?? "unknown",
    };
  } catch {
    return null;
  }
}
